/**
 * Hook to fire the two-step "offer reminder" tour after funnel creation
 * when the funnel contains an offer step or a BOGO checkout. Step 1
 * anchors to the node in the canvas via [data-tour="offer-step"]; step 2
 * anchors to the offer-config section inside the step editor side panel
 * via [data-tour="offer-config"]. When the user advances from step 1 to
 * step 2 we auto-select the target step so the editor panel opens and
 * the second anchor exists in the DOM.
 *
 * Cooperates with useFunnelEditorTour — runs only after designs exist
 * (the same condition that suppresses the editor-setup tour), so the
 * two tours never overlap.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useNextStep } from 'nextstepjs';
import type { FunnelNode } from '../types';

const OFFER_TOUR_SHOWN_KEY = 'funnel_offer_reminder_shown';

// Local StepType uses 'offer' for upsell + downsell — no separate values.
const OFFER_STEP_TYPES = new Set(['offer']);

interface UseFunnelOfferReminderTourOptions {
    funnelId: string | undefined;
    nodes: FunnelNode[] | undefined;
    isLoading: boolean;
    onResetView?: () => void;
    /** Called when the tour advances to step 2 so the editor panel opens
     *  and [data-tour="offer-config"] becomes mountable. */
    onSelectStep?: (stepId: string) => void;
}

interface UseFunnelOfferReminderTourReturn {
    onTourComplete: (tourName: string | null) => void;
    onTourSkip: (step: number, tourName: string | null) => void;
    onTourStepChange: (step: number, tourName: string | null) => void;
}

function hasOfferStep(nodes: FunnelNode[] | undefined): boolean {
    if (!nodes || nodes.length === 0) return false;
    return nodes.some((n) => OFFER_STEP_TYPES.has(n.type));
}

/** BOGO checkouts carry their offers inline in stepConfig.checkoutOffers,
 *  so the merchant still needs the same reminder — anchored to the checkout
 *  node instead of an offer node. Detected via the bogo-checkout plugin
 *  prefix the wizard's BOGO override produces. */
function hasBogoCheckout(nodes: FunnelNode[] | undefined): boolean {
    if (!nodes || nodes.length === 0) return false;
    return nodes.some((n: any) => {
        if (n.type !== 'checkout') return false;
        const pluginId = n.config?.pluginId || '';
        return pluginId.startsWith('bogo-checkout');
    });
}

/** Find the node whose editor we want to open at step 2 — offer node first,
 *  then BOGO checkout. Returns undefined if neither exists. */
function findOfferTargetNode(nodes: FunnelNode[] | undefined): FunnelNode | undefined {
    if (!nodes || nodes.length === 0) return undefined;
    const offer = nodes.find((n) => OFFER_STEP_TYPES.has(n.type));
    if (offer) return offer;
    return nodes.find((n: any) => {
        if (n.type !== 'checkout') return false;
        const pluginId = n.config?.pluginId || '';
        return pluginId.startsWith('bogo-checkout');
    });
}

/** Mirror of hasAnyDesignConfigured in useFunnelEditorTour — used to know
 *  whether the editor-setup tour would suppress itself. We only fire the
 *  offer reminder once designs exist, so the two tours never overlap. */
function hasAnyDesignConfigured(nodes: FunnelNode[] | undefined): boolean {
    if (!nodes || nodes.length === 0) return false;
    return nodes.some((node: any) => {
        if (node.type === 'storefront_shopify') return true;
        if (node.kind === 'condition') return true;
        if (node.config?.variant) return true;
        if (node.variants && node.variants.length > 0) {
            return node.variants.some((variant: any) => variant.config?.variant);
        }
        return false;
    });
}

function wasOfferTourShown(funnelId: string): boolean {
    try {
        return localStorage.getItem(`${OFFER_TOUR_SHOWN_KEY}_${funnelId}`) === 'true';
    } catch {
        return false;
    }
}

function markOfferTourShown(funnelId: string): void {
    try {
        localStorage.setItem(`${OFFER_TOUR_SHOWN_KEY}_${funnelId}`, 'true');
    } catch {
        // ignore
    }
}

export function useFunnelOfferReminderTour({
    funnelId,
    nodes,
    isLoading,
    onResetView,
    onSelectStep,
}: UseFunnelOfferReminderTourOptions): UseFunnelOfferReminderTourReturn {
    const { startNextStep, closeNextStep } = useNextStep();
    const hasStartedTour = useRef(false);
    const targetStepIdRef = useRef<string | undefined>(undefined);
    const onSelectStepRef = useRef(onSelectStep);
    onSelectStepRef.current = onSelectStep;

    useEffect(() => {
        const log = (msg: string, extra?: any) =>
            console.log('[funnel-offer-reminder]', msg, extra ?? '');
        if (isLoading) return log('skip: isLoading');
        if (!funnelId) return log('skip: no funnelId');
        if (!nodes) return log('skip: no nodes');
        if (hasStartedTour.current) return log('skip: already started');
        if (wasOfferTourShown(funnelId)) return log('skip: previously shown', funnelId);
        const hasOffer = hasOfferStep(nodes);
        const hasBogo = hasBogoCheckout(nodes);
        if (!hasOffer && !hasBogo) {
            return log('skip: no offer + no BOGO', { hasOffer, hasBogo });
        }
        if (!hasAnyDesignConfigured(nodes)) return log('skip: no design configured');

        const target = findOfferTargetNode(nodes);
        targetStepIdRef.current = target?.id;
        log('all gates passed → scheduling tour start', {
            funnelId,
            targetStepId: target?.id,
        });

        hasStartedTour.current = true;
        // Mark shown the moment we trigger it. The hook's onTourComplete /
        // onTourSkip aren't reliably wired to NextStepReact's onComplete /
        // onSkip (those go to useOnboardingTour), so we can't trust those
        // paths to persist the flag. Treating start-of-tour as "shown" is
        // good enough — the user shouldn't see it twice regardless of how
        // they dismiss it.
        markOfferTourShown(funnelId);

        // Reset the canvas so the target node is visible, then fire the tour.
        setTimeout(() => {
            if (onResetView) onResetView();
            setTimeout(() => {
                console.log('[funnel-offer-reminder]', 'calling startNextStep');
                startNextStep('funnel-offer-reminder');
            }, 150);
        }, 800);
        // No cleanup-cancel: if the effect re-runs after we set hasStartedTour,
        // we don't want to abort the timer — the next run will early-return.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [funnelId, nodes, isLoading]);

    const onTourComplete = useCallback(
        (tourName: string | null) => {
            if (tourName === 'funnel-offer-reminder' && funnelId) {
                markOfferTourShown(funnelId);
                closeNextStep();
            }
        },
        [funnelId, closeNextStep],
    );

    const onTourSkip = useCallback(
        (_step: number, tourName: string | null) => {
            if (tourName === 'funnel-offer-reminder' && funnelId) {
                markOfferTourShown(funnelId);
                closeNextStep();
            }
        },
        [funnelId, closeNextStep],
    );

    /** When the user advances from step 1 (canvas anchor) to step 2 (panel
     *  anchor) we need the editor side panel open so the [data-tour="offer-config"]
     *  selector exists. Auto-select the target step on advance. */
    const onTourStepChange = useCallback(
        (step: number, tourName: string | null) => {
            if (tourName !== 'funnel-offer-reminder') return;
            // nextstepjs may report step as 0-indexed or 1-indexed depending on
            // the call site — accept both for the second step.
            const isOnConfigStep = step === 1 || step === 2;
            if (!isOnConfigStep) return;
            const targetId = targetStepIdRef.current;
            if (!targetId) return;
            onSelectStepRef.current?.(targetId);
        },
        [],
    );

    return { onTourComplete, onTourSkip, onTourStepChange };
}
